import * as React from 'react';
import { useQuery } from 'react-apollo';
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { ErrorDisplay } from 'shared/components';
import {
  USER_FEATURE_FLAGS_QUERY,
  FeatureFlagsQueryResult,
} from 'users/queries';

interface Props {
  userId: string;
}

interface QueryRes {
  getUserFeatureFlags: FeatureFlagsQueryResult;
}

interface QueryVars {
  userId: string;
}

interface FlagRow {
  key: string;
  value: string;
}

/**
 * The backend returns each flag as either a bare key (an enabled boolean
 * flag) or `key:value` (a non-boolean flag). Boolean flags that are off for
 * the user are not returned at all.
 */
export function parseFeatureFlags(featureFlags: string[]): FlagRow[] {
  return featureFlags
    .map((flag) => {
      const separator = flag.indexOf(':');
      if (separator === -1) return { key: flag, value: 'true' };
      return {
        key: flag.slice(0, separator),
        value: flag.slice(separator + 1),
      };
    })
    .sort((a, b) => a.key.localeCompare(b.key));
}

export function filterFeatureFlags(rows: FlagRow[], filter: string): FlagRow[] {
  const needle = filter.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((row) => row.key.toLowerCase().includes(needle));
}

export default function FeatureFlagsList(props: Props) {
  const { userId } = props;
  const [filter, setFilter] = React.useState('');
  const { data, error, loading, refetch } = useQuery<QueryRes, QueryVars>(
    USER_FEATURE_FLAGS_QUERY,
    {
      variables: { userId },
      fetchPolicy: 'network-only',
    },
  );

  if (loading && !data) return <Typography>Loading...</Typography>;
  if (error) return <ErrorDisplay>{error.message}</ErrorDisplay>;
  if (!data) return null;

  const allRows = parseFeatureFlags(data.getUserFeatureFlags.featureFlags);
  const rows = filterFeatureFlags(allRows, filter);

  return (
    <div>
      <Typography variant="body2" gutterBottom>
        LaunchDarkly flags as evaluated by the backend for this user. Boolean
        flags that are off for this user are not listed.
      </Typography>
      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
        <TextField
          label="Filter by flag name"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          inputProps={{ 'aria-label': 'Filter by flag name' }}
          style={{ marginRight: 16, minWidth: 300 }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={() => refetch()}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>
      <Paper style={{ marginTop: 16 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Flag</TableCell>
              <TableCell>Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={2}>
                  {allRows.length === 0
                    ? 'No flags enabled for this user'
                    : 'No flags match the filter'}
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell>
                  <code>{row.key}</code>
                </TableCell>
                <TableCell>{row.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </div>
  );
}
